/**
 * Cart Operations Service
 * Handles cart item operations like add, remove, update quantity
 * Extracted from the main cart context for better separation of concerns
 */

import type { CartItem } from '../../app/hiv-test/types';
import { customToast } from '../../app/components/shared/CustomToast';

export class CartOperationsService {
  /**
   * Adds an item to the cart
   */
  addItemToCart(currentCart: CartItem[], newItem: CartItem): CartItem[] {
    const existingItemIndex = currentCart.findIndex(
      item => item.id === newItem.id
    );

    if (existingItemIndex > -1) {
      // Clone the cart to avoid mutating state directly
      const updatedCart = currentCart.map((item, index) => {
        if (index === existingItemIndex) {
          return {
            ...item,
            quantity: item.quantity + 1,
          };
        }
        return item;
      });

      return updatedCart;
    } else {
      // Ensure new item starts with quantity 1
      return [...currentCart, { ...newItem, quantity: 1 }];
    }
  }

  /**
   * Removes an item from the cart
   */
  removeItemFromCart(currentCart: CartItem[], itemId: string): CartItem[] {
    return currentCart.filter(item => item.id !== itemId);
  }

  /**
   * Updates the quantity of an item in the cart
   */
  updateItemQuantity(
    currentCart: CartItem[],
    itemId: string,
    quantity: number
  ): CartItem[] {
    return currentCart.map(item =>
      item.id === itemId ? { ...item, quantity } : item
    );
  }

  /**
   * Clears all items from the cart
   */
  clearCart(): CartItem[] {
    return [];
  }

  /**
   * Shows success toast when item is added to cart
   */
  showAddToCartSuccessToast(): void {
    customToast.success('Item added to cart!');
  }

  /**
   * Validates cart item data
   */
  validateCartItem(item: CartItem): boolean {
    if (!item.id) {
      console.error('Cart item missing ID:', item);
      return false;
    }

    if (!item.quantity || item.quantity <= 0) {
      console.error('Cart item has invalid quantity:', item);
      return false;
    }

    return true;
  }

  /**
   * Gets total number of items in cart
   */
  getTotalItemCount(cart: CartItem[]): number {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }

  /**
   * Checks if cart is empty
   */
  isCartEmpty(cart: CartItem[]): boolean {
    return cart.length === 0;
  }

  /**
   * Finds an item in the cart by ID
   */
  findItemById(cart: CartItem[], itemId: string): CartItem | undefined {
    return cart.find(item => item.id === itemId);
  }
}
